import { Dispatch, SetStateAction } from "react";
import { ensureError } from "shared-resources";

interface MessageBody {
    type: string;
    candidate?: string;
    sdpMid?: string;
    sdpMLineIndex?: number;
}

export class PeerConnection {
    private signaling = new BroadcastChannel("webrtc");
    private localStream: MediaStream | null = null;
    private peerConnection = new RTCPeerConnection();

    constructor(private remoteVideoOutput: HTMLVideoElement, private setActiveCall: Dispatch<SetStateAction<boolean>>) {
        this.signaling.onmessage = (event: MessageEvent) => {
            if (!this.localStream) {
                console.log("Not ready yet on this side");
                return;
            }

            switch (event.data.type) {
                case "offer":
                    this.handleOffer(event.data);
                    break;
                case "answer":
                    this.handleAnswer(event.data);
                    break;
                case "candidate":
                    this.handleCandidate(event.data);
                    break;
                case "ready":
                    if (this.peerConnection.iceConnectionState === "connected") {
                        console.log("already in a call, ignoring");
                        return;
                    }
                    this.makeCall();
                    break;
                case "bye":
                    if (this.peerConnection.iceConnectionState === "connected") {
                        this.hangup();
                    }
                    break;
                default:
                    console.log("unhandled case", event);
                    break;
            }
        };
    }

    initialize = (localStream: MediaStream) => {
        this.localStream = localStream;
        this.signaling.postMessage({ type: "ready" });
    };

    hangup = () => {
        if (this.peerConnection.iceConnectionState === "connected") {
            this.peerConnection.removeEventListener("icecandidate", this.addCandidateListener);
            this.peerConnection.removeEventListener("track", this.addTrackListener);
            this.peerConnection.close();
        }

        this.setActiveCall(false);
        this.localStream = null;
        this.signaling.postMessage({ type: "bye" });

        console.log('setting localconnection and remoteconnection to "closed"');
    };

    private addCandidateListener = (event: RTCPeerConnectionIceEvent) => {
        const message: MessageBody = {
            type: "candidate",
            candidate: event.candidate?.candidate,
            sdpMid: event.candidate?.sdpMid ?? undefined,
            sdpMLineIndex: event.candidate?.sdpMLineIndex ?? undefined,
        };

        this.signaling.postMessage(message);
    };

    private addTrackListener = (event: RTCTrackEvent): void => {
        this.remoteVideoOutput.srcObject = event.streams[0];
    };

    private createPeerConnection = () => {
        if (this.peerConnection.iceConnectionState === "closed") {
            this.peerConnection = new RTCPeerConnection();
        }

        this.peerConnection.addEventListener("icecandidate", this.addCandidateListener);

        this.peerConnection.addEventListener("track", this.addTrackListener);

        this.localStream!.getTracks().forEach((track) =>
            this.peerConnection.addTrack(track, this.localStream as MediaStream)
        );
    };

    private makeCall = async (): Promise<void> => {
        try {
            this.createPeerConnection();
            const offer = await this.peerConnection.createOffer();

            this.signaling.postMessage({ type: "offer", sdp: offer.sdp });
            await this.peerConnection.setLocalDescription(offer);
        } catch (err) {
            console.log(`Error when making call: ${ensureError(err).message}`);
        }
    };

    private handleOffer = async (offer: RTCSessionDescription): Promise<void> => {
        if (this.peerConnection.iceConnectionState === "connected") {
            console.log("existing peerConnection");
            return;
        }
        this.createPeerConnection();

        await this.peerConnection.setRemoteDescription(offer);
        const answer = await this.peerConnection.createAnswer();
        this.signaling.postMessage({ type: "answer", sdp: answer.sdp });
        await this.peerConnection.setLocalDescription(answer);
    };

    private handleAnswer = async (answer: RTCSessionDescription) => {
        if (this.peerConnection.iceConnectionState === "failed") {
            console.log("no peerconnection");
            return;
        }
        await this.peerConnection.setRemoteDescription(answer);
    };

    private handleCandidate = async (iceCandidate: RTCIceCandidateInit | RTCIceCandidate): Promise<void> => {
        if (this.peerConnection.iceConnectionState === "failed") {
            console.log("no peerConnection");
            return;
        }

        if (!iceCandidate.candidate) {
            await this.peerConnection.addIceCandidate();
        } else {
            await this.peerConnection.addIceCandidate(iceCandidate);
        }
    };
}
